import { LoaderCircle, Sparkles } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, type AiReprocessRun } from "@/shared/api/api-client";
import { ApiError, errorMessage } from "@/shared/api/http";
import { AppLink } from "@/shared/components/AppLink";
import { ApiState } from "@/shared/components/ApiState";
import { usePolling } from "@/shared/hooks/usePolling";
import { formatDateTime } from "@/shared/utils/format";
import { formatDuration, parseServerTime } from "@/features/uploads/upload-metrics";
import { AiReprocessProgress } from "@/features/mentions/AiReprocessProgress";
import {
  describeTarget,
  isAiReprocessTracked,
  runStatusMeta,
  taskLabels
} from "@/features/mentions/ai-reprocess-view";

/** O teto do servidor (`GET /mentions/ai-reprocess?limit=`). */
const RUNS_LIMIT = 50;

/**
 * Os reprocessamentos por IA da empresa, com o andamento de cada um —
 * o lugar pra ver "como está indo" a qualquer momento, como a tela de
 * Uploads é pros envios (2026-10-14). Até aqui o andamento só existia no
 * banner da tela de menções, e só da execução mais recente: saindo de
 * lá, ele sumia.
 *
 * Só aplicações: a amostra não grava nada e vive dentro do diálogo. A
 * lista se atualiza sozinha enquanto alguma execução anda (inclusive a
 * `ERRORED` que a fila vai retomar) e para quando todas terminam.
 */
export function ReprocessRunsPage({
  token,
  runHref
}: {
  token: string;
  /** Destino do detalhe de uma execução. Link, e não callback
   *  (2026-10-26): vale "abrir em nova aba". */
  runHref: (runId: number) => string;
}) {
  // O clique no CARD inteiro também abre o detalhe; o título é o link de
  // verdade (teclado, clique do meio, nova aba) e para a propagação.
  const navigate = useNavigate();
  const [runs, setRuns] = useState<AiReprocessRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      setRuns(await apiClient.listAiReprocess(token, RUNS_LIMIT));
      setError(null);
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
      setError(errorMessage(failure, "Não foi possível carregar os reprocessamentos."));
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const tracked = (runs ?? []).filter(isAiReprocessTracked);
  usePolling({
    enabled: tracked.length > 0,
    onTick: () => void reload(),
    signature: tracked
      .map(run => `${run.id}:${run.status}:${run.counters.pending}`)
      .join(","),
    pausedUntil
  });

  return (
    <div className="uploads-page">
      <section className="card upload-history-card">
        <div className="card-title">
          <h3>
            <Sparkles size={17} />
            Reprocessamentos por IA{runs ? ` · ${runs.length}` : ""}
          </h3>
        </div>
        {error && (
          <ApiState kind="error" onRetry={() => void reload()}>
            {error}
          </ApiState>
        )}
        {runs === null && !error && <ApiState kind="loading">Carregando…</ApiState>}
        {runs !== null && runs.length === 0 && (
          <div className="upload-history-empty">
            <Sparkles size={26} />
            <strong>Nenhum reprocessamento ainda</strong>
            <span>
              Selecione menções na tela de Análises e menções e escolha
              “Reprocessar com IA”.
            </span>
          </div>
        )}
        {runs !== null && runs.length > 0 && (
          <div className="upload-card-grid">
            {runs.map(run => (
              <RunCard key={run.id} run={run} href={runHref(run.id)} onOpen={() => navigate(runHref(run.id))} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function RunCard({ run, href, onOpen }: { run: AiReprocessRun; href: string; onOpen: () => void }) {
  const meta = runStatusMeta(run);
  const tracked = isAiReprocessTracked(run);
  const requested = parseServerTime(run.requested_at);
  const who = run.requested_by_name || run.requested_by;
  return (
    // Clique no card abre o detalhe — mesmo desenho do card de upload
    // (ver o comentário de lá sobre por que é `onClick` no `<article>`).
    <article
      className="upload-card reprocess-card"
      onClick={onOpen}
      onKeyDown={event => {
        if (event.key === "Enter") onOpen();
      }}
      tabIndex={0}
      aria-label={`Reprocessamento #${run.id}`}
    >
      <div className="upload-card-head">
        <span className={`upload-file-icon ${meta.tone}`}>
          {tracked ? <LoaderCircle className="spin" size={17} /> : <Sparkles size={17} />}
        </span>
        <span className="upload-card-name">
          <strong>
            <AppLink className="reprocess-card-link" to={href} stopPropagation>
              Reprocessamento #{run.id}
            </AppLink>
          </strong>
          <small>{taskLabels(run)}</small>
        </span>
        <span className={`upload-status ${meta.tone}`}>{meta.label}</span>
      </div>
      <AiReprocessProgress run={run} label={`Reprocessamento #${run.id}`} />
      <div className="reprocess-card-facts">
        <span>{describeTarget(run)}{run.include_reviewed ? " · inclui revisadas" : ""}</span>
        <span>
          {who}
          {requested !== null && ` · ${formatDateTime(new Date(requested))}`}
          {runDurationLabel(run)}
        </span>
      </div>
      {run.status === "ERRORED" && !run.retrying && run.message && (
        <p className="reprocess-card-error">{run.message}</p>
      )}
    </article>
  );
}

/** " · levou 4 min", ou " · rodando há 2 min"; vazio na fila. */
export function runDurationLabel(run: AiReprocessRun, nowMs = Date.now()): string {
  const started = parseServerTime(run.started_at);
  if (started === null) return "";
  const finished = parseServerTime(run.finished_at);
  if (finished !== null && !isAiReprocessTracked(run)) {
    return ` · levou ${formatDuration((finished - started) / 1000)}`;
  }
  return ` · rodando há ${formatDuration((nowMs - started) / 1000)}`;
}
